<template>
  <nav class="bg-primary text-primary-foreground shadow-md">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Left side - Logo and Brand -->
        <div class="flex items-center gap-3">
          <Mountain class="w-8 h-8" />
          <router-link
            :to="localePath('Home')"
            class="text-xl font-semibold hover:opacity-90 transition-opacity"
          >
            Hai Afară!
          </router-link>
        </div>

        <!-- Desktop Navigation - Right side -->
        <div class="hidden md:flex items-center gap-6">
          <router-link
            :to="localePath('Home')"
            class="px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            {{ $t('nav.home') }}
          </router-link>
          <router-link
            :to="localePath('Zones')"
            class="px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            {{ $t('nav.zones') }}
          </router-link>

          <template v-if="!authStore.isAuthenticated">
            <router-link
              :to="loginLink"
              class="px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
            >
              {{ $t('nav.login') }}
            </router-link>
            <router-link
              :to="localePath('Register')"
              class="px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
            >
              {{ $t('nav.register') }}
            </router-link>
          </template>
          <template v-else>
            <router-link
              to="/dashboard"
              class="px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
            >
              {{ $t('nav.dashboard') }}
            </router-link>

            <div class="flex items-center gap-3 ml-2 pl-6 border-l border-primary-foreground/20">
              <router-link
                to="/dashboard/profile"
                :title="authStore.user?.username || authStore.user?.email"
              >
                <img
                  v-if="authStore.user?.avatar_urls?.thumbnail"
                  :src="authStore.user.avatar_urls.thumbnail"
                  class="w-9 h-9 rounded-full object-cover border-2 border-primary-foreground/30"
                  :alt="authStore.user.username"
                />
                <span
                  v-else
                  class="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/20 border-2 border-primary-foreground/30 text-primary-foreground text-sm font-bold leading-none"
                >
                  {{ authStore.user?.username ? authStore.user.username[0].toUpperCase() : '?' }}
                </span>
              </router-link>

              <button
                class="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-primary-foreground/10 transition-colors"
                @click="handleLogout"
              >
                <LogOut class="w-4 h-4" />
                {{ $t('nav.logout') }}
              </button>
            </div>
          </template>
        </div>

        <!-- Mobile menu button -->
        <button
          class="md:hidden p-2 rounded-md hover:bg-primary-foreground/10 transition-colors"
          :aria-label="$t('nav.toggleMenu')"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <X v-if="mobileMenuOpen" class="w-6 h-6" />
          <MenuIcon v-else class="w-6 h-6" />
        </button>
      </div>
    </div>

    <!-- Mobile menu -->
    <div
      v-if="mobileMenuOpen"
      class="md:hidden pb-4 border-t border-primary-foreground/20 mt-2 pt-4"
    >
      <div class="px-4 flex flex-col space-y-3">
        <router-link
          :to="localePath('Home')"
          class="px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
        >
          {{ $t('nav.home') }}
        </router-link>
        <router-link
          :to="localePath('Zones')"
          class="px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
        >
          {{ $t('nav.zones') }}
        </router-link>

        <template v-if="!authStore.isAuthenticated">
          <router-link
            :to="loginLink"
            class="px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            {{ $t('nav.login') }}
          </router-link>
          <router-link
            :to="localePath('Register')"
            class="px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            {{ $t('nav.register') }}
          </router-link>
        </template>
        <template v-else>
          <router-link
            to="/dashboard"
            class="px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
          >
            {{ $t('nav.dashboard') }}
          </router-link>

          <router-link
            to="/dashboard/profile"
            class="flex items-center gap-3 pt-3 border-t border-primary-foreground/20 hover:bg-primary-foreground/10 rounded-md p-2 transition-colors"
            :title="authStore.user?.username || authStore.user?.email"
          >
            <img
              v-if="authStore.user?.avatar_urls?.thumbnail"
              :src="authStore.user.avatar_urls.thumbnail"
              class="w-9 h-9 rounded-full object-cover border-2 border-primary-foreground/30"
              :alt="authStore.user?.username"
            />
            <span
              v-else
              class="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-foreground/20 border-2 border-primary-foreground/30 text-primary-foreground text-sm font-bold leading-none"
            >
              {{ authStore.user?.username ? authStore.user.username[0].toUpperCase() : '?' }}
            </span>
            <span class="text-sm">{{ authStore.user?.username || authStore.user?.email }}</span>
          </router-link>

          <button
            class="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-primary-foreground/10 transition-colors"
            @click="handleLogout"
          >
            <LogOut class="w-4 h-4" />
            {{ $t('nav.logout') }}
          </button>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useLocalePath } from '../composables/useLocalePath'
import { useLoginLink } from '../composables/useLoginLink'
import { Mountain, LogOut, Menu as MenuIcon, X } from 'lucide-vue-next'

const router = useRouter()
const authStore = useAuthStore()
const { localePath } = useLocalePath()
const { loginLink } = useLoginLink()
const mobileMenuOpen = ref(false)

const handleLogout = async () => {
  await authStore.logout()
  router.push(localePath('Login'))
  mobileMenuOpen.value = false
}
</script>
